import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, } from 'react';
import { InteractionManager, View } from 'react-native';
import { getReward, getRewards } from 'actions';
import { AppView, CardView, Indicator, SCROLL_INDICATOR_INSETS, SettingsItem, Spacer, TextButton, rem, withAnimatedNavigationHeader, ButtonGroup, } from 'design-system';
import Button, { HEIGHT } from 'design-system/Button';
import Text from 'design-system/Text';
import { getRewardTextToShow, useRewards, } from 'features/rewards/hooks/useRewards';
import { muteAll, useFetchOld } from 'hooks';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import Amplitude from 'utils/amplitude';
import useColors from 'hooks/useColors';
import StatusBar from 'design-system/StatusBar';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import Animated, { useAnimatedScrollHandler } from 'react-native-reanimated';
import createStyleSheets from 'utils/createStyleSheets';
import useStyles from 'hooks/useStyles';
import FastImageCommon from 'common/FastImageCommon';
import Squircle from 'design-system/Squircle';
import { useMarginBottom } from 'hooks/useMarginBottom';
import { selectUserTier } from 'features/premium/selectors/tier';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
import { HELP_ARTICLES_KEY } from 'features/help/constants';
import RewardLogo from './components/RewardLogo';
import Disclaimer from './components/Disclaimer';
import { RewardImageBackgroundGroup } from './components/RewardImageBackgroundGroup';
import { isWeb } from '../../constants';
const strings = {
    howDoesItWork: 'How does it work',
    about: 'About',
    moreLikeThis: 'More Like this',
    getCashbackNow: 'Get cashback now',
};
export const RewardScreen = withAnimatedNavigationHeader(({ route, navigation, scrollY }) => {
    const [, , , fetch] = useFetchOld(muteAll);
    const { openRewardLink } = useRewards();
    const colors = useColors();
    const dispatch = useAppDispatch();
    const styles = useStyles(styleSet);
    const rewards = useAppSelector((store) => store.rewards.rewards);
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const reward = 'reward' in route.params ? route.params?.reward : undefined;
    const userTier = useAppSelector(selectUserTier);
    const contentContainerStyles = useMarginBottom('paddingBottom', HEIGHT + rem(32) + WEB_EXTRA_BOTTOM_SPACE);
    useLayoutEffect(() => {
        navigation.setOptions({
            headerTitle: reward?.title || '',
            headerRight: () => (<HelpScreenButton articlesKey={HELP_ARTICLES_KEY.CASHBACK}/>),
        });
    }, [reward]);
    useEffect(() => {
        InteractionManager.runAfterInteractions(() => {
            if (!rewards?.length) {
                dispatch(getRewards());
            }
            if ('id' in route.params && route.params?.id && !reward?.id) {
                fetch(getReward(route.params.id)).then((response) => {
                    if (response) {
                        navigation.setParams({
                            reward: response,
                        });
                    }
                });
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const prevUserTier = useRef(userTier);
    useEffect(() => {
        if (userTier !== prevUserTier.current && reward?.id) {
            dispatch(getReward(reward.id));
            prevUserTier.current = userTier;
        }
    }, [reward?.id, userTier]);
    const onClaimReward = useCallback(() => {
        if (!reward)
            return;
        openRewardLink(reward, 'RewardPage.GoToPartner');
    }, [openRewardLink, reward]);
    const openReward = useCallback((data) => {
        navigation.push('Reward', { reward: data });
        Amplitude.logEvent('Open Reward', { source: 'reward_screen', data });
    }, []);
    const partner = reward?.partner;
    const similarRewards = useMemo(() => {
        if (reward) {
            return rewards
                .filter(({ id, type }) => type === reward?.type && id !== reward.id)
                .slice(0, 8);
        }
        return [];
    }, [reward, rewards]);
    const rewardToShow = getRewardTextToShow(reward);
    const onScroll = useAnimatedScrollHandler((event) => {
        // eslint-disable-next-line no-param-reassign
        scrollY.value = event.contentOffset.y;
    }, []);
    const renderIcon = useCallback((text) => () => (<View style={styles.row}>
            <Squircle width={rem(32)} height={rem(32)} color={colors.buttons.brandGroup}>
              <Text Text-14 OnBrand>
                {text}
              </Text>
            </Squircle>
            <Spacer w={16}/>
          </View>), [colors.buttons.brandGroup, styles.row]);
    const handleSeeMore = useCallback(() => {
        if (reward) {
            navigation.push('RewardCategory', {
                rewardType: reward.type,
            });
        }
    }, [reward]);
    if (!reward?.id) {
        return (<AppView>
          <StatusBar barStyle="dark-content" animated/>
          <View style={styles.container}>
            <Indicator dark/>
          </View>
        </AppView>);
    }
    return (<AppView type="secondary" withFrame={false}>
        <StatusBar barStyle="dark-content" animated/>
        <Animated.ScrollView onScroll={onScroll} scrollEventThrottle={16} contentContainerStyle={[
            contentContainerStyles,
            paddingHorizontalStyle,
        ]} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS}>
          <CardView>
            {/* A few offers have no image; they start with the title instead of an empty square */}
            {reward?.imageUrl ? (<View style={styles.imgContainer}>
                <FastImageCommon resizeMode="cover" url={reward.imageUrl} imageStyle={styles.img}/>
              </View>) : null}
            <Spacer h={16}/>
            <View style={styles.infoContainer}>
              <View style={styles.flex}>
                <Text Text-20>{reward?.title || ''}</Text>
                <Text TextThin-16 Secondary>
                  {rewardToShow}
                </Text>
              </View>
              <RewardLogo data={reward}/>
            </View>
            <Spacer h={16}/>
          </CardView>

          {!!reward?.description && (<CardView noMarginTop title="How does it work">
              <View style={styles.descContainer}>
                <Text TextThin-14 Secondary>
                  {reward.description}
                </Text>
              </View>
            </CardView>)}

          {!!partner?.description && (<CardView noMarginTop title="About">
              <View style={styles.descContainer}>
                <Text TextThin-14 Secondary>
                  {partner.description}
                </Text>
              </View>
            </CardView>)}

          <CardView noMarginTop title="How to redeem">
            <View style={styles.redeemSteps}>
              <SettingsItem title="Click get cashback" renderIcon={renderIcon('1')} description="The store link will open"/>
              <SettingsItem title="Select your items" renderIcon={renderIcon('2')} description="Choose what to buy"/>
              <SettingsItem title="Purchase" renderIcon={renderIcon('3')} description="Complete the checkout in the same session"/>
            </View>
          </CardView>

          {!!similarRewards.length && (<CardView title="More like this" noMarginTop>
              <RewardImageBackgroundGroup rewards={similarRewards} onRewardSelect={openReward}/>
              <View style={styles.textButtonContainer}>
                <TextButton small text="See more" onPress={handleSeeMore}/>
              </View>
            </CardView>)}

          <Disclaimer />
        </Animated.ScrollView>
        <ButtonGroup type="floating" style={styles.floatingButtons}>
          <Button brand onPress={onClaimReward} title={strings.getCashbackNow}/>
        </ButtonGroup>
      </AppView>);
}, ({ colors }) => ({
    hasCustomTitle: true,
    headerColorOutputRange: [
        colors.background.dark,
        colors.cards.onDark,
    ],
}));
// On web the page ends at the window edge, so the button sits higher off it
const WEB_EXTRA_BOTTOM_SPACE = isWeb ? rem(8) : 0;
const styleSet = createStyleSheets(() => ({
    floatingButtons: {
        ...(isWeb && { bottom: rem(24) }),
    },
    container: {
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
    },
    infoContainer: {
        flexDirection: 'row',
        paddingHorizontal: rem(16),
    },
    flex: {
        flex: 1,
    },
    imgContainer: {
        aspectRatio: 1,
        borderRadius: rem(16),
    },
    img: {
        width: '100%',
        height: '100%',
    },
    descContainer: {
        padding: rem(16),
    },
    row: {
        alignItems: 'center',
        flexDirection: 'row',
    },
    // On web a settings row is inset by 8 and expects the card to add the other 8
    redeemSteps: {
        ...(isWeb && { paddingHorizontal: rem(8) }),
    },
    textButtonContainer: {
        alignItems: 'center',
        marginBottom: rem(16),
    },
}));
export default RewardScreen;
